import weatherZonePolygonsById from 'data/Massive_Unionized_MultiPolygons.json';
import { getAlertWeatherZoneIds } from 'features/Weather/features/WeatherAlerts/utils/alertData.utils';
import normalizePolygon, { calculateDistanceInMiles, flattenPolygon } from 'utils/maps/geometry.utils';

const DISTANCE_FOR_UNKNOWN_LOCATION_MILES = 100000;
const weatherZoneLocationCache = new Map();

/**
 * Alerts can describe an affected location in two ways. Some alerts include their own polygon,
 * while others list weather-zone IDs whose predefined polygons live in the application data.
 * This file converts both forms into the same location shape for alert lists and weather maps.
 */

/**
 * @typedef {[number, number]} LatitudeLongitude
 * A point written in the application's `[latitude, longitude]` order.
 *
 * @typedef {LatitudeLongitude[]} AlertPolygon
 * Coordinate points that draw one affected area on a map.
 *
 * @typedef {object} AlertLocation
 * @property {'alert-polygon'|'weather-zone'} source - Where the polygon data came from.
 * @property {AlertPolygon[]} polygons - Affected-area polygons in app coordinate order.
 * @property {[LatitudeLongitude, LatitudeLongitude]|null} bounds - Two corners used to fit every polygon in the map view.
 * @property {LatitudeLongitude|null} center - Point used for map selection and distance sorting.
 */

/**
 * Checks whether a value can be used as a latitude and longitude pair.
 *
 * Polygon cleanup and distance calculation use this guard before passing coordinates to map or
 * geometry code. Numeric strings are accepted because some providers send coordinates as text.
 *
 * @param {unknown} value - Possible `[latitude, longitude]` pair.
 * @returns {boolean} True when both coordinate values are numeric.
 */
const isValidLatitudeLongitudePair = (value) => {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    Number.isFinite(Number(value[0])) &&
    Number.isFinite(Number(value[1]))
  );
};

/**
 * Calculates the average latitude and longitude from a collection of coordinate points.
 *
 * Polygon-center helpers use this shared calculation for the points inside one polygon and for
 * the center points produced from several polygons.
 *
 * @param {LatitudeLongitude[]} coordinates - Coordinate points to average.
 * @returns {LatitudeLongitude|null} Average latitude and longitude, or null when none exist.
 */
const getCenterOfCoordinates = (coordinates) => {
  if (!Array.isArray(coordinates) || !coordinates.length) return null;

  const totals = coordinates.reduce(
    ([latitudeTotal, longitudeTotal], [latitude, longitude]) => [
      latitudeTotal + Number(latitude),
      longitudeTotal + Number(longitude),
    ],
    [0, 0],
  );

  return [totals[0] / coordinates.length, totals[1] / coordinates.length];
};

/**
 * Calculates the center point of one polygon.
 *
 * Weather maps use this point to focus on an affected area. The current calculation averages the
 * polygon's coordinate points so callers do not need to understand the underlying geometry.
 *
 * @param {AlertPolygon} polygon - Coordinate points that draw one affected area.
 * @returns {LatitudeLongitude|null} Center point, or null for an empty polygon.
 */
const getCenterOfPolygon = (polygon) => getCenterOfCoordinates(polygon);

/**
 * Calculates one center point for a collection of polygons.
 *
 * Each polygon contributes one center point so a polygon containing more coordinates does not
 * automatically outweigh the other affected areas.
 *
 * @param {AlertPolygon[]} polygons - Affected-area polygons to combine.
 * @returns {LatitudeLongitude|null} Combined center point, or null when no polygons exist.
 */
const getCenterOfPolygons = (polygons) => {
  if (!Array.isArray(polygons)) return null;

  const polygonCenters = polygons.map(getCenterOfPolygon).filter(Boolean);
  return getCenterOfCoordinates(polygonCenters);
};

/**
 * Calculates the southwest and northeast corners surrounding every polygon.
 *
 * Alert maps pass these two corners to `BaseMap` so it can fit the complete affected area in the
 * visible map. Invalid polygon data returns null instead of breaking map rendering.
 *
 * @param {AlertPolygon[]} polygons - Affected-area polygons to measure.
 * @returns {[LatitudeLongitude, LatitudeLongitude]|null} Map bounds, or null when unavailable.
 */
const getBoundsOfPolygons = (polygons) => {
  try {
    return normalizePolygon(polygons)?.bounds ?? null;
  } catch {
    return null;
  }
};

/**
 * Converts polygon coordinates to numbers and removes polygons that cannot describe an area.
 *
 * Alert polygon parsing uses this after `normalizePolygon` has separated a provider value into
 * individual polygons. Each polygon needs at least three usable points before Leaflet can draw it.
 *
 * @param {unknown[]} polygons - Polygons that may contain strings or malformed points.
 * @returns {AlertPolygon[]} Clean numeric polygons suitable for map rendering.
 */
const normalizePolygonCoordinates = (polygons) => {
  if (!Array.isArray(polygons)) return [];

  return polygons
    .filter(Array.isArray)
    .map((polygon) =>
      polygon
        .filter(isValidLatitudeLongitudePair)
        .map(([latitude, longitude]) => [Number(latitude), Number(longitude)]),
    )
    .filter((polygon) => polygon.length >= 3);
};

/**
 * Loads the predefined polygons belonging to one weather-zone ID.
 *
 * A weather-zone ID identifies an official warning area, such as an NWS UGC zone. The polygon
 * dataset stores points as `[longitude, latitude]`, so this function reverses each point into the
 * `[latitude, longitude]` order used by the rest of the application.
 *
 * @param {string} weatherZoneId - Identifier used to look up an official warning area.
 * @returns {AlertPolygon[]} Known polygons for the weather zone, or an empty array.
 */
const loadWeatherZonePolygons = (weatherZoneId) => {
  const storedPolygons = weatherZonePolygonsById[weatherZoneId];
  if (!Array.isArray(storedPolygons)) return [];

  return storedPolygons
    .map((storedPolygon) => flattenPolygon(storedPolygon))
    .filter(Array.isArray)
    .map((storedPolygon) =>
      storedPolygon
        .filter(isValidLatitudeLongitudePair)
        .map(([longitude, latitude]) => [Number(latitude), Number(longitude)]),
    )
    .filter((polygon) => polygon.length >= 3);
};

/**
 * Returns prepared location details for one weather zone and remembers the result.
 *
 * Many active alerts can reference the same weather-zone ID. The severe-weather map calls this
 * during area preparation, so caching avoids repeatedly parsing the same large polygon data.
 * Missing zones are cached as null for the same reason.
 *
 * @param {string} weatherZoneId - Identifier for the predefined warning area to prepare.
 * @returns {AlertLocation|null} Prepared weather-zone location, or null when it is unknown.
 */
const getCachedWeatherZoneLocation = (weatherZoneId) => {
  if (weatherZoneLocationCache.has(weatherZoneId)) {
    return weatherZoneLocationCache.get(weatherZoneId);
  }

  const polygons = loadWeatherZonePolygons(weatherZoneId);
  const location = polygons.length
    ? {
        source: 'weather-zone',
        polygons,
        bounds: getBoundsOfPolygons(polygons),
        center: getCenterOfPolygons(polygons),
      }
    : null;

  weatherZoneLocationCache.set(weatherZoneId, location);
  return location;
};

/**
 * Converts a polygon included in an alert response into map-ready location details.
 *
 * Providers may send the polygon as an array, JSON text, or space-delimited coordinate text.
 * Alert processing and severe-weather map preparation use this function when the alert supplies
 * its own exact affected-area polygon.
 *
 * @param {Array|string|null|undefined} polygon - Raw value from the alert's `polygon` field.
 * @returns {AlertLocation|null} Prepared alert-polygon location, or null when parsing fails.
 */
export const getLocationFromAlertPolygon = (polygon) => {
  try {
    const parsedPolygon = normalizePolygon(polygon);
    if (!parsedPolygon) return null;

    const polygons = normalizePolygonCoordinates(parsedPolygon.polygons);
    if (!polygons.length) return null;

    const polygonMeasurements = normalizePolygon(polygons);
    if (!polygonMeasurements) return null;

    return {
      source: 'alert-polygon',
      polygons,
      bounds: polygonMeasurements.bounds,
      center: polygonMeasurements.center,
    };
  } catch {
    return null;
  }
};

/**
 * Combines the predefined polygons for every weather-zone ID referenced by an alert.
 *
 * Alerts often list multiple official warning areas. Alert processing and the severe-weather map
 * use this function to turn those IDs into one location containing all affected polygons.
 * Duplicate IDs are ignored, and unknown IDs do not prevent known areas from rendering.
 *
 * @param {string|string[]} weatherZoneIds - Official warning-area identifiers to combine.
 * @returns {AlertLocation|null} Combined weather-zone location, or null when no polygon is known.
 */
export const getLocationFromWeatherZoneIds = (weatherZoneIds) => {
  const requestedWeatherZoneIds = Array.isArray(weatherZoneIds) ? weatherZoneIds : [weatherZoneIds];
  const weatherZoneLocations = [...new Set(requestedWeatherZoneIds.filter(Boolean))]
    .map(getCachedWeatherZoneLocation)
    .filter(Boolean);

  if (!weatherZoneLocations.length) return null;

  const polygons = weatherZoneLocations.flatMap((location) => location.polygons);
  return {
    source: 'weather-zone',
    polygons,
    bounds: getBoundsOfPolygons(polygons),
    center: getCenterOfPolygons(polygons),
  };
};

/**
 * Chooses the most precise location available for an alert.
 *
 * Alert lists and maps prefer a polygon supplied directly by the provider because it describes
 * the event itself. When that polygon is missing or invalid, this function falls back to the
 * predefined polygons for the alert's weather-zone IDs.
 *
 * @param {object|null|undefined} alert - Alert returned by an alert provider.
 * @returns {AlertLocation|null} Best available prepared location for the alert.
 */
export const getBestAvailableAlertLocation = (alert) => {
  return (
    getLocationFromAlertPolygon(alert?.polygon) ??
    getLocationFromWeatherZoneIds(getAlertWeatherZoneIds(alert))
  );
};

/**
 * Calculates how many miles separate the user from an alert's representative center.
 *
 * Alert lists use this value for distance display and sorting. A deliberately large fallback
 * keeps alerts with missing coordinates at the end instead of treating them as nearby.
 *
 * @param {LatitudeLongitude|null} alertCenter - Representative center of the affected area.
 * @param {LatitudeLongitude|undefined} userCoordinates - User location in app coordinate order.
 * @returns {number} Distance in miles, or the unknown-distance fallback.
 */
const getMilesFromUserToAlert = (alertCenter, userCoordinates) => {
  if (
    !isValidLatitudeLongitudePair(alertCenter) ||
    !isValidLatitudeLongitudePair(userCoordinates)
  ) {
    return DISTANCE_FOR_UNKNOWN_LOCATION_MILES;
  }

  return calculateDistanceInMiles(
    Number(userCoordinates[0]),
    Number(userCoordinates[1]),
    Number(alertCenter[0]),
    Number(alertCenter[1]),
  );
};

/**
 * Adds the location fields expected by existing alert UI to a copy of an alert.
 *
 * This is the compatibility entry point used by alert lists, widgets, Redux processing, and map
 * consumers. `processedPolygon`, `processedCenter`, `processedBounds`, and `distanceToUser` keep
 * their legacy names so those callers do not need to understand how provider polygons and weather
 * zones are resolved. Bounds remain limited to provider polygons to preserve existing behavior.
 *
 * @param {object} alert - Alert returned by an alert provider.
 * @param {LatitudeLongitude} [userCoordinates] - User location in `[latitude, longitude]` order.
 * @returns {object} Alert copy with its best available location and distance fields.
 */
export const getAlertWithLocationDetails = (alert, userCoordinates) => {
  const location = getBestAvailableAlertLocation(alert);
  if (!location) {
    return {
      ...alert,
      processedPolygon: null,
      processedCenter: null,
      distanceToUser: DISTANCE_FOR_UNKNOWN_LOCATION_MILES,
    };
  }

  const alertWithLocation = {
    ...alert,
    processedPolygon: location.polygons,
    processedCenter: location.center,
    distanceToUser: getMilesFromUserToAlert(location.center, userCoordinates),
  };

  return location.source === 'alert-polygon'
    ? { ...alertWithLocation, processedBounds: location.bounds }
    : alertWithLocation;
};
